/**
 * Chart palettes.
 *
 * Two categorical palettes, one per surface. Both come from a validated
 * reference instance rather than being hand-picked, and both pass the six
 * categorical checks (lightness band, chroma floor, protan/deutan CVD
 * separation, normal-vision separation, contrast vs surface):
 *
 *   light on #ffffff — worst adjacent CVD ΔE 9.1, normal-vision ΔE 19.6.
 *     Three slots (aqua, yellow, magenta) sit under 3:1 against white, so the
 *     block ships the data table on by default as the required relief channel.
 *   dark on #1a1a19  — worst adjacent CVD ΔE 8.4, normal-vision ΔE 19.3, and
 *     every slot clears 3:1.
 *
 * The slot ORDER is the colourblind-safety mechanism, not a mood choice — hues
 * are handed out in sequence and never reordered or cycled. Past eight series
 * there is no ninth hue: a generated one is indistinguishable from an existing
 * slot under CVD, so the editor caps the series count instead.
 *
 * Mirrored by Chart\ChartData::PALETTES in PHP. Change both together.
 */

import { resolvePresetValue } from '../../../utils/helpers';

export const PALETTES = {
	light: [
		'#2a78d6',
		'#eb6834',
		'#1baf7a',
		'#eda100',
		'#e87ba4',
		'#008300',
		'#4a3aa7',
		'#e34948',
	],
	dark: [
		'#3987e5',
		'#d95926',
		'#199e70',
		'#c98500',
		'#d55181',
		'#008300',
		'#9085e9',
		'#e66767',
	],
};

/**
 * Chart chrome — the ink that is not data. Recessive by design: the gridline is
 * one step off the surface, the axis text is muted.
 */
export const CHROME = {
	light: {
		surface: '#ffffff',
		grid: '#e1e0d9',
		axis: '#c3c2b7',
		ink: '#0b0b0b',
		muted: '#898781',
	},
	dark: {
		surface: '#1a1a19',
		grid: '#2c2c2a',
		axis: '#383835',
		ink: '#ffffff',
		muted: '#a5a49c',
	},
};

/** Hard ceiling on categorical slots — see the note above. */
export const MAX_SERIES = PALETTES.light.length;

/**
 * Resolve the colour for a series.
 *
 * Colour follows the entity, never its rank: the slot is the series' own index,
 * so hiding or reordering series never repaints the survivors.
 *
 * @param {Object} series      Series object, whose `color` wins when set.
 * @param {number} index       Series index.
 * @param {string} paletteName Palette key.
 * @return {string} CSS colour.
 */
export function seriesColor( series, index, paletteName ) {
	if ( series && series.color ) {
		// Palette picks arrive as `var:preset|color|slug`, which is not CSS.
		return resolvePresetValue( series.color );
	}
	const palette = PALETTES[ paletteName ] || PALETTES.light;
	return palette[ index % palette.length ];
}

/**
 * Pick white or ink for text set on top of a filled mark.
 *
 * A label inside a coloured fill is the one place text may leave the text
 * tokens, and it only works if the choice follows the fill's luminance — white
 * on this palette's yellow is unreadable, ink on its violet likewise.
 *
 * Mirrors Chart\ChartData::contrast_ink() in PHP.
 *
 * @param {string} hex Fill colour.
 * @return {string} Text colour.
 */
export function contrastInk( hex ) {
	const match = /^#([0-9a-f]{6})$/i.exec( String( hex ).trim() );

	if ( ! match ) {
		// A CSS variable or a named colour cannot be measured here; ink on a
		// light surface is the safer guess than white on an unknown fill.
		return CHROME.light.ink;
	}

	const channels = match[ 1 ].match( /../g ).map( ( pair ) => {
		const value = parseInt( pair, 16 ) / 255;
		return value <= 0.03928
			? value / 12.92
			: Math.pow( ( value + 0.055 ) / 1.055, 2.4 );
	} );

	const luminance =
		0.2126 * channels[ 0 ] + 0.7152 * channels[ 1 ] + 0.0722 * channels[ 2 ];

	// 0.189 is where the two contrast ratios cross for this ink: below it
	// white wins, above it the ink does. Derived, not eyeballed.
	return luminance > 0.189 ? CHROME.light.ink : '#ffffff';
}
